import { computed, toValue } from 'vue';
import { useProForecastStore } from '@/stores/ProForecastStore';
import { useUserStore } from '@/stores/UserStore';

// Pro Forecast is a per-spot premium model the models API does not return, so
// it has to be injected separately: a selector-shaped option for ModelSelector,
// and a data-bearing model object for the graph's additionalModels. Both share
// the same availability/access gating, kept here so the two stay in sync.
const PRO_FORECAST_MODEL_ID = -9999;
const PRO_FORECAST_MEMBER_LEVEL = 49;

// `spotDataObject` may be a ref, a getter, or a plain object (resolved via toValue).
export const useProForecastModel = (spotDataObject, storeKey = null) => {
  const proForecastStore = useProForecastStore(storeKey);
  const userStore = useUserStore();

  const spot = computed(() => toValue(spotDataObject));
  const proForecastModelId = computed(() => spot.value?.proForecastIdIfExists);
  const upgradeAvailable = computed(() => userStore.memberLevel < PRO_FORECAST_MEMBER_LEVEL);

  const showProForecastModel = computed(() => {
    return !!(spot.value?.proForecastIdIfExists
      && spot.value?.spotProducts?.includes(3)
      && proForecastStore.canShowProForecast);
  });

  // Entry merged into ModelSelector's additionalModels (no model_data needed).
  const selectorOption = computed(() => {
    if (!showProForecastModel.value) return null;

    return {
      model_id: PRO_FORECAST_MODEL_ID,
      model_short_name: 'Pro Forecast',
      is_premium: true,
      is_pro_only: true,
      upgrade_available: upgradeAvailable.value,
    };
  });

  // Data-bearing model object for the graph's additionalModels.
  async function getModelData() {
    let spotForecastModelData = null;

    if (userStore.memberLevel >= PRO_FORECAST_MEMBER_LEVEL) {
      await proForecastStore.getProForecast(proForecastModelId.value);
      spotForecastModelData = proForecastStore.buildSpotForecastModelData(spot.value.spotId, spot.value.localUTCOffset);
    }

    if (!spotForecastModelData) {
      console.warn('No Pro Forecast Model Data available');
      return null;
    }

    return {
      is_premium: true,
      is_pro_only: true,
      model_name: 'Pro Forecast',
      model_id: PRO_FORECAST_MODEL_ID,
      model_color: '513A85',
      model_data: spotForecastModelData,
      is_upgrade_available: upgradeAvailable.value,
    };
  }

  return { showProForecastModel, proForecastModelId, selectorOption, getModelData };
};
